import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ArrowRight, ShieldCheck, Award, Users } from "lucide-react";
import Seo from "@/components/Seo";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { StatusBadge } from "@/components/learning/ProgrammeCard";
import { api } from "@/lib/api";
import { SCHOOLS, PATHWAYS, FLAGSHIP_PATHWAY, FIELD_EXPERIENCES, LEARNING_FAQ } from "@/lib/learning";

const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />
    {children}
  </p>
);

function Hero() {
  return (
    <section data-testid="learning-hero" className="relative min-h-[85vh] flex items-end bg-ptf-primary text-ptf-bg grain overflow-hidden">
      <img src="/dawn/vor-raft-sunset.jpg" alt="A Planet Trotter field session on the river at sunset" className="absolute inset-0 h-full w-full object-cover opacity-45" />
      <div className="absolute inset-0 bg-gradient-to-t from-ptf-primary via-ptf-primary/55 to-ptf-primary/20" />
      <div className="relative mx-auto max-w-[1600px] w-full px-5 sm:px-8 lg:px-12 pb-14 md:pb-20 pt-40">
        <Eyebrow light>Planet Trotter Field Learning</Eyebrow>
        <MaskText
          tag="h1"
          lines={["Learn. Train.", "Explore. Serve."]}
          className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.92] tracking-tight"
        />
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-ptf-bg/80 leading-relaxed">
            Practical learning in adventure, conservation, communities, responsible
            exploration and field skills, delivered through classrooms, camps and
            real-world experiences.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <PtfButton to="/learning/training" variant="accent" data-testid="hero-explore-training">Explore Training</PtfButton>
            <PtfButton to="/volunteer" variant="outlineLight" data-testid="hero-volunteer">Volunteer With Us</PtfButton>
            <PtfButton to="/learning/upcoming" variant="outlineLight" data-testid="hero-upcoming">View Upcoming Programmes</PtfButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Intro() {
  return (
    <section className="bg-ptf-bg py-20 md:py-28">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <Reveal><Eyebrow>Why Planet Trotter learning?</Eyebrow></Reveal>
          <MaskText
            lines={["Learning beyond", "the classroom."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight text-ptf-primary"
          />
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <p className="text-lg text-ptf-text/80 leading-relaxed">
              Planet Trotter's approach to learning is built around experience. We
              bring together practical skills, outdoor education, conservation,
              adventure, community engagement, leadership and responsible exploration.
            </p>
            <p className="mt-5 text-ptf-text/70 leading-relaxed">
              Some programmes are classroom-based. Some are field-based. Some combine
              both. The objective is not simply to complete a course. It is to learn a
              skill, apply it responsibly and understand the world in which that skill
              matters.
            </p>
            <p className="mt-5 font-serif-ed italic text-ptf-secondary text-lg">
              Learn. Practise. Experience. Serve. Lead. Create impact.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function SchoolsGrid({ counts }) {
  return (
    <section data-testid="training-schools" className="bg-white border-y border-ptf-primary/10 py-20 md:py-28">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <Reveal><Eyebrow>Explore our training schools</Eyebrow></Reveal>
            <MaskText
              lines={["Seventeen schools.", "One field."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
          </div>
          <PtfButton to="/learning/training" variant="primary" data-testid="schools-view-all">Browse the Full Catalogue</PtfButton>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-ptf-primary/10 border border-ptf-primary/10">
          {SCHOOLS.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 4) * 0.04} className="h-full">
              <Link to={`/learning/training?category=${s.slug}`} data-testid={`school-${s.slug}`} className="group bg-white p-6 h-full flex flex-col hover:bg-ptf-bg transition-colors duration-300">
                <span className="font-display text-2xl text-ptf-primary/25">{s.num}</span>
                <h3 className="mt-2 font-display uppercase text-lg tracking-tight text-ptf-primary leading-tight group-hover:text-ptf-secondary transition-colors">{s.name}</h3>
                <p className="mt-2 text-xs text-ptf-text/65 leading-relaxed flex-1">{s.blurb}</p>
                <span className="mt-4 text-[0.6rem] uppercase tracking-[0.16em] text-ptf-text/50">{counts[s.slug] || 0} programmes <ArrowUpRight className="inline h-3 w-3" /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FirstAidFeature() {
  return (
    <section data-testid="first-aid-feature" className="bg-ptf-primary text-ptf-bg py-20 md:py-28 grain">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow light>Featured school · First Aid, CPR & Rescue</Eyebrow></Reveal>
          <MaskText
            lines={["Skills that matter when", "help is not immediately", "available."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight"
          />
          <Reveal delay={0.15}>
            <p className="mt-6 text-ptf-bg/75 leading-relaxed max-w-xl">
              From building your own home first-aid kit to CPR awareness, rescue
              fundamentals, wilderness first aid and disaster preparedness: learn what
              to do before professional help arrives.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Basic First Aid", "DIY First Aid", "CPR & Emergency Response", "Rescue Fundamentals", "Wilderness First Aid", "Disaster Preparedness"].map((c) => (
                <span key={c} className="text-[0.65rem] uppercase tracking-[0.12em] border border-ptf-bg/25 px-3 py-1.5 text-ptf-bg/80">{c}</span>
              ))}
            </div>
            <p className="mt-5 text-xs text-ptf-bg/50 leading-relaxed max-w-xl inline-flex gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
              Training content and certification are delivered according to the
              applicable instructor standard and programme protocol. These modules do
              not replace professional medical treatment.
            </p>
            <div className="mt-7">
              <PtfButton to="/learning/training?category=first-aid-cpr-rescue" variant="accent" data-testid="first-aid-cta">Explore First Aid & Rescue</PtfButton>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <img src="/dawn/vor-medicines.jpg" alt="Medicines and first-aid supplies documented during Project Dawn field work" className="w-full object-cover border border-ptf-bg/15" loading="lazy" />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function DocumentedArchive({ programmes }) {
  const docs = programmes.filter((p) => p.status === "DELIVERED").slice(0, 12);
  if (!docs.length) return null;
  return (
    <section data-testid="documented-archive" className="bg-ptf-bg py-20 md:py-28">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <Reveal><Eyebrow>Field Learning · Documented</Eyebrow></Reveal>
        <MaskText
          lines={["Already taught,", "already recorded."]}
          className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
        />
        <Reveal delay={0.15}>
          <p className="mt-5 text-ptf-text/70 leading-relaxed max-w-2xl">
            These programmes are supported by Planet Trotter's existing field
            documentation. Historical activity is shown as history; it is not
            converted into a current course.
          </p>
        </Reveal>
        <div className="mt-10 border-t-2 border-ptf-primary/15">
          {docs.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 6) * 0.03}>
              <Link to={`/learning/programme/${p.slug}`} data-testid={`documented-${p.slug}`} className="group grid grid-cols-12 items-center gap-3 py-4 border-b border-ptf-primary/10 hover:bg-white transition-colors">
                <div className="col-span-12 sm:col-span-4">
                  <p className="font-display uppercase text-lg tracking-tight text-ptf-primary group-hover:text-ptf-secondary transition-colors">{p.title}</p>
                </div>
                <div className="col-span-8 sm:col-span-5">
                  <p className="text-[0.62rem] uppercase tracking-[0.12em] text-ptf-text/55">{p.documented_when}</p>
                </div>
                <div className="col-span-4 sm:col-span-3 text-right">
                  <StatusBadge status="DELIVERED" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PathwaysPreview() {
  return (
    <section data-testid="pathways-preview" className="bg-white border-y border-ptf-primary/10 py-20 md:py-28">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <Reveal><Eyebrow>Learning pathways</Eyebrow></Reveal>
            <MaskText
              lines={["From first module", "to field-ready."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
          </div>
          <PtfButton to="/learning/pathways" variant="primary" data-testid="pathways-view-all">All Pathways</PtfButton>
        </div>
        <Reveal>
          <Link to="/learning/pathways" data-testid="flagship-pathway-card" className="group block bg-ptf-secondary text-ptf-bg p-8 md:p-10 mb-6 hover:bg-ptf-primary transition-colors duration-300">
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-accent">Signature pathway</p>
            <h3 className="mt-2 font-display uppercase text-3xl md:text-4xl tracking-tight">{FLAGSHIP_PATHWAY.name}</h3>
            <p className="mt-3 text-sm text-ptf-bg/75 max-w-2xl leading-relaxed">{FLAGSHIP_PATHWAY.blurb}</p>
            <p className="mt-4 text-[0.62rem] uppercase tracking-[0.12em] text-ptf-bg/60 leading-relaxed">{FLAGSHIP_PATHWAY.steps.join("  ·  ")}</p>
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PATHWAYS.slice(0, 3).map((pw, i) => (
            <Reveal key={pw.num} delay={i * 0.06}>
              <Link to="/learning/pathways" className="group block border border-ptf-primary/12 p-6 h-full hover:border-ptf-primary/40 transition-colors">
                <span className="font-display text-2xl text-ptf-primary/25">{pw.num}</span>
                <h3 className="mt-1 font-display uppercase text-xl tracking-tight text-ptf-primary group-hover:text-ptf-secondary transition-colors">{pw.name}</h3>
                <p className="mt-2 text-xs text-ptf-text/60 leading-relaxed">{pw.steps.join(" · ")}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FieldExperiencesPreview() {
  return (
    <section data-testid="field-experiences-preview" className="bg-ptf-bg py-20 md:py-28">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <Reveal><Eyebrow>Learn in the field</Eyebrow></Reveal>
            <MaskText
              lines={["Not conventional", "courses."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
          </div>
          <PtfButton to="/learning/field" variant="primary" data-testid="field-view-all">All Field Experiences</PtfButton>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FIELD_EXPERIENCES.slice(0, 3).map((f, i) => (
            <Reveal key={f.name} delay={i * 0.06}>
              <Link to="/learning/field" className="group block bg-white border border-ptf-primary/12 p-6 h-full hover:border-ptf-primary/40 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display uppercase text-xl tracking-tight text-ptf-primary group-hover:text-ptf-secondary transition-colors">{f.name}</h3>
                  <StatusBadge status={f.status} />
                </div>
                <p className="mt-2 text-xs text-ptf-text/60 leading-relaxed">{f.activities}</p>
                <p className="mt-3 text-[0.62rem] uppercase tracking-[0.12em] text-ptf-text/50">{f.location} · {f.duration}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingBands() {
  return (
    <>
      <section className="bg-ptf-secondary text-ptf-bg py-16 md:py-20">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Users, title: "Volunteer with us", text: "Your time can become field action across ten volunteer areas.", to: "/volunteer", cta: "Volunteer", tid: "band-volunteer" },
            { icon: Award, title: "Verify a certificate", text: "Every Planet Trotter certificate carries a number that can be checked publicly.", to: "/verify", cta: "Verify a Certificate", tid: "band-verify" },
            { icon: ArrowRight, title: "Train your team", text: "Custom programmes for schools, corporates, NGOs, clubs and institutions.", to: "/learning/institutional", cta: "Request a Custom Programme", tid: "band-institutional" },
          ].map(({ icon: Icon, title, text, to, cta, tid }) => (
            <div key={title} className="border-t border-ptf-bg/20 pt-6 flex flex-col">
              <Icon className="h-6 w-6 text-ptf-accent" />
              <h3 className="mt-3 font-display uppercase text-2xl tracking-tight">{title}</h3>
              <p className="mt-2 text-sm text-ptf-bg/70 leading-relaxed flex-1">{text}</p>
              <Link to={to} data-testid={tid} className="mt-5 inline-flex items-center gap-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-ptf-accent hover:text-ptf-bg transition-colors">
                {cta} <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </section>
      <section data-testid="learning-faq" className="bg-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow>Common questions</Eyebrow></Reveal>
          <MaskText
            lines={["Asked plainly,", "answered plainly."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <div className="mt-10 space-y-0">
            {LEARNING_FAQ.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.04}>
                <div className="py-6 border-t border-ptf-primary/12 grid grid-cols-1 md:grid-cols-12 gap-3">
                  <h3 className="md:col-span-5 font-display uppercase text-xl tracking-tight text-ptf-primary">{f.q}</h3>
                  <p className="md:col-span-7 text-sm text-ptf-text/70 leading-relaxed">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default function Learning() {
  const { data: programmes = [] } = useQuery({ queryKey: ["training"], queryFn: async () => (await api.get("/training")).data });
  const counts = programmes.reduce((acc, p) => { acc[p.category] = (acc[p.category] || 0) + 1; return acc; }, {});
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: LEARNING_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    { "@context": "https://schema.org", "@type": "Organization", name: "Planet Trotter Foundation", url: "https://planettrotterfoundation.com" },
  ];
  return (
    <>
      <Seo
        title="Field Learning: Training, Courses & Volunteering"
        path="/learning"
        description="Planet Trotter Field Learning: practical training in first aid and CPR awareness, wilderness survival, trekking, kayaking, conservation, responsible tourism, leadership and more, plus volunteer opportunities across India."
        jsonLd={jsonLd}
      />
      <Hero />
      <Intro />
      <SchoolsGrid counts={counts} />
      <FirstAidFeature />
      <DocumentedArchive programmes={programmes} />
      <PathwaysPreview />
      <FieldExperiencesPreview />
      <ClosingBands />
    </>
  );
}
